import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
  TextField,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AttachMoney,
  Assignment,
  Receipt,
  Today,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import invoiceService from '../services/invoice.service';
import { requestService } from '../services/requestService';
import employeeService from '../services/employee.service';
import paymentService from '../services/payment.service';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Date range states for charts
  const [paymentsDateFrom, setPaymentsDateFrom] = useState<string>('');
  const [paymentsDateTo, setPaymentsDateTo] = useState<string>('');
  const [requestsDateFrom, setRequestsDateFrom] = useState<string>('');
  const [requestsDateTo, setRequestsDateTo] = useState<string>('');
  const [packageServicesDateFrom, setPackageServicesDateFrom] = useState<string>('');
  const [packageServicesDateTo, setPackageServicesDateTo] = useState<string>('');
  
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalInvoices: 0,
    todayInvoices: 0,
    totalRequests: 0,
    todayRequests: 0,
    totalPayments: 0,
    todayPayments: 0,
    paymentsOverTime: [] as any[],
    packageServicesData: [] as any[],
    requestsData: [] as any[],
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];
        const todayStart = new Date(today).toISOString(); // Start of today in ISO format
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999); // End of today
        const todayEndISO = todayEnd.toISOString();

        // Build payment filters
        const paymentFilters: any = { 'pagination[pageSize]': 500, 'sort[0]': 'paymentDate:desc' };
        if (paymentsDateFrom) {
          paymentFilters['filters[paymentDate][$gte]'] = paymentsDateFrom;
        }
        if (paymentsDateTo) {
          paymentFilters['filters[paymentDate][$lte]'] = paymentsDateTo;
        }

        // Build request filters
        const requestFilters: any = { 'pagination[pageSize]': 500, 'sort[0]': 'createdAt:desc' };
        if (requestsDateFrom) {
          requestFilters['filters[createdAt][$gte]'] = requestsDateFrom;
        }
        if (requestsDateTo) {
          requestFilters['filters[createdAt][$lte]'] = requestsDateTo;
        }

        // Build package services request filters
        const packageServicesFilters: any = { 'pagination[pageSize]': 500, 'sort[0]': 'createdAt:desc' };
        if (packageServicesDateFrom) {
          packageServicesFilters['filters[createdAt][$gte]'] = packageServicesDateFrom;
        }
        if (packageServicesDateTo) {
          packageServicesFilters['filters[createdAt][$lte]'] = packageServicesDateTo;
        }

        // Fetch all data including historical data for charts
        console.log('🔍 Dashboard: Fetching today\'s data with date:', today, 'Start:', todayStart, 'End:', todayEndISO);
        
        const [
          employeesResponse,
          invoicesResponse,
          invoicesTodayResponse,
          requestsResponse,
          requestsTodayResponse,
          paymentsResponse,
          paymentsTodayResponse,
          allPaymentsResponse,
          allRequestsResponse,
          packageServicesRequestsResponse,
        ] = await Promise.all([
          employeeService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          invoiceService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          invoiceService.getAll({ 
            'pagination[pageSize]': 1,
            'filters[createdAt][$gte]': todayStart,
            'filters[createdAt][$lte]': todayEndISO,
          }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          requestService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ meta: { pagination: { total: 0 } } })),
          requestService.getAll({ 
            'pagination[pageSize]': 1,
            'filters[createdAt][$gte]': todayStart,
            'filters[createdAt][$lte]': todayEndISO,
          }).catch(() => ({ meta: { pagination: { total: 0 } } })),
          paymentService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          paymentService.getAll({ 
            'pagination[pageSize]': 1,
            'filters[createdAt][$gte]': todayStart,
            'filters[createdAt][$lte]': todayEndISO,
          }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          paymentService.getAll(paymentFilters).catch(() => ({ data: { data: [] } })),
          requestService.getAll(requestFilters).catch(() => ({ data: [] })),
          requestService.getAll(packageServicesFilters).catch(() => ({ data: [] })),
        ]);

        console.log('📊 Dashboard Results:', {
          totalInvoices: invoicesResponse.data?.meta?.pagination?.total,
          todayInvoices: invoicesTodayResponse.data?.meta?.pagination?.total,
          totalRequests: requestsResponse.meta?.pagination?.total,
          todayRequests: requestsTodayResponse.meta?.pagination?.total,
        });

        // Fetch a few invoices to check their actual dates
        const sampleInvoices = await invoiceService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'createdAt:desc' }).catch(() => ({ data: { data: [] } }));
        console.log('📋 Sample invoices (most recent):', sampleInvoices.data?.data?.map((inv: any) => ({
          id: inv.id,
          number: inv.attributes?.invoiceNumber,
          createdAt: inv.attributes?.createdAt,
          invoiceDate: inv.attributes?.invoiceDate,
        })));

        // Fetch a few requests to check their actual dates
        const sampleRequests = await requestService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'createdAt:desc' }).catch(() => ({ data: [] }));
        const requestsList = sampleRequests.data || [];
        console.log('📋 Sample requests (most recent):', requestsList.map((req: any) => ({
          id: req.id,
          number: req.attributes?.requestNumber,
          createdAt: req.attributes?.createdAt,
        })));

        // Process real payments data for chart
        const paymentsData = allPaymentsResponse.data?.data || [];
        const monthlyPayments: { [key: string]: number } = {};
        
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        // Determine date range for chart
        let startDate: Date;
        let endDate: Date = new Date();
        
        if (paymentsDateFrom && paymentsDateTo) {
          startDate = new Date(paymentsDateFrom);
          endDate = new Date(paymentsDateTo);
        } else if (paymentsDateFrom) {
          startDate = new Date(paymentsDateFrom);
        } else if (paymentsDateTo) {
          startDate = new Date(endDate);
          startDate.setMonth(startDate.getMonth() - 5); // Default to last 6 months
        } else {
          // Default to last 6 months
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 5);
        }
        
        // Generate month list based on date range
        const monthsList = [];
        const current = new Date(startDate);
        current.setDate(1); // Start from first day of month
        
        while (current <= endDate) {
          const monthKey = `${monthNames[current.getMonth()]} ${current.getFullYear()}`;
          const monthShort = monthNames[current.getMonth()];
          monthsList.push({ 
            key: monthKey, 
            display: monthShort, 
            year: current.getFullYear(), 
            month: current.getMonth() 
          });
          monthlyPayments[monthKey] = 0;
          current.setMonth(current.getMonth() + 1);
        }
        
        // Aggregate payments by month
        paymentsData.forEach((payment: any) => {
          const paymentDate = new Date(payment.attributes?.paymentDate || payment.paymentDate || payment.createdAt);
          const monthKey = `${monthNames[paymentDate.getMonth()]} ${paymentDate.getFullYear()}`;
          const amount = parseFloat(payment.attributes?.amount || payment.amount || 0);
          
          if (monthlyPayments[monthKey] !== undefined) {
            monthlyPayments[monthKey] += amount;
          }
        });
        
        const paymentsOverTime = monthsList.map(m => ({
          month: m.display,
          amount: Math.round(monthlyPayments[m.key] || 0)
        }));

        // Process package services data from requests (group by serviceType)
        const servicesMap = new Map();
        const packageServicesRequestsData = packageServicesRequestsResponse.data || [];
        
        packageServicesRequestsData.forEach((request: any) => {
          const serviceType = request.attributes?.serviceType || request.serviceType;
          if (serviceType) {
            servicesMap.set(
              serviceType,
              (servicesMap.get(serviceType) || 0) + 1
            );
          }
        });

        const packageServicesData = Array.from(servicesMap.entries()).map(([name, value]) => ({
          name,
          value,
        }));

        // Process real requests data by status
        const statusCounts: { [key: string]: number } = {
          'Pending': 0,
          'Assigned': 0,
          'Active': 0,
          'Invoice Creation': 0,
          'Invoice Sent': 0,
          'Payment Received': 0,
          'Completed': 0,
        };
        
        const allRequestsData = allRequestsResponse.data || [];
        allRequestsData.forEach((request: any) => {
          const status = request.attributes?.status || request.status || 'pending';
          const statusLabel = status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
          
          if (status === 'pending') statusCounts['Pending']++;
          else if (status === 'assigned') statusCounts['Assigned']++;
          else if (status === 'active') statusCounts['Active']++;
          else if (status === 'invoice_creation') statusCounts['Invoice Creation']++;
          else if (status === 'invoice_sent') statusCounts['Invoice Sent']++;
          else if (status === 'payment_received') statusCounts['Payment Received']++;
          else if (status === 'completed') statusCounts['Completed']++;
        });
        
        const requestsData = Object.entries(statusCounts).map(([status, count]) => ({
          status,
          count
        }));

        setStats({
          totalEmployees: employeesResponse.data?.meta?.pagination?.total || 0,
          totalInvoices: invoicesResponse.data?.meta?.pagination?.total || 0,
          todayInvoices: invoicesTodayResponse.data?.meta?.pagination?.total || 0,
          totalRequests: requestsResponse.meta?.pagination?.total || 0,
          todayRequests: requestsTodayResponse.meta?.pagination?.total || 0,
          totalPayments: paymentsResponse.data?.meta?.pagination?.total || 0,
          todayPayments: paymentsTodayResponse.data?.meta?.pagination?.total || 0,
          paymentsOverTime,
          packageServicesData,
          requestsData,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [paymentsDateFrom, paymentsDateTo, requestsDateFrom, requestsDateTo, packageServicesDateFrom, packageServicesDateTo]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];

  const StatCard = ({ title, value, subtitle, icon, color, onClick }: any) => (
    <Card 
      elevation={2}
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 6,
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {value}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {title}
        </Typography>
        {subtitle && (
          <Chip 
            label={subtitle} 
            size="small" 
            color="primary" 
            sx={{ mt: 1, height: 20, fontSize: '0.7rem' }}
          />
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('navigation.dashboard')}
          </Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          {t('dashboard.title')}
        </Typography>

        {/* Stats Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4 
        }}>
          <StatCard
            title={t('dashboard.totalEmployees')}
            value={stats.totalEmployees}
            icon={<People />}
            color="primary.main"
            onClick={() => navigate('/employees')}
          />
          <StatCard
            title={t('dashboard.totalInvoices')}
            value={stats.totalInvoices}
            subtitle={`${t('common.today')}: ${stats.todayInvoices}`}
            icon={<Receipt />}
            color="info.main"
            onClick={() => navigate('/invoices')}
          />
          <StatCard
            title={t('dashboard.totalRequests')}
            value={stats.totalRequests}
            subtitle={`${t('common.today')}: ${stats.todayRequests}`}
            icon={<Assignment />}
            color="warning.main"
            onClick={() => navigate('/requests')}
          />
          <StatCard
            title={t('dashboard.totalPayments')}
            value={stats.totalPayments}
            subtitle={`${t('common.today')}: ${stats.todayPayments}`}
            icon={<AttachMoney />}
            color="success.main"
            onClick={() => navigate('/payments')}
          />
        </Box>

        {/* Today's Stats Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 3,
          mb: 4 
        }}>
          <StatCard
            title={t("Today's Invoices")}
            value={stats.todayInvoices}
            icon={<Today />}
            color="secondary.main"
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              navigate(`/invoices?dateFrom=${today}&dateTo=${today}`);
            }}
          />
          <StatCard
            title={t("Today's Requests")}
            value={stats.todayRequests}
            icon={<Today />}
            color="error.main"
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              navigate(`/requests?dateFrom=${today}&dateTo=${today}`);
            }}
          />
        </Box>

        {/* Charts Row 1 */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 3,
          mb: 4 
        }}>
          {/* Sales/Payments Over Time Chart */}
          <Card elevation={2}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {t('dashboard.salesPaymentsOverTime')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  type="date"
                  label={t('common.from')}
                  value={paymentsDateFrom}
                  onChange={(e) => setPaymentsDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  type="date"
                  label={t('common.to')}
                  value={paymentsDateTo}
                  onChange={(e) => setPaymentsDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPaymentsDateFrom('');
                    setPaymentsDateTo('');
                  }}
                >
                  {t('common.reset')}
                </Button>
              </Box>
            </Box>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.paymentsOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                      name="Amount (OMR)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Package Services Pie Chart */}
          <Card elevation={2}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Package Services
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  type="date"
                  label={t('common.from')}
                  value={packageServicesDateFrom}
                  onChange={(e) => setPackageServicesDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  type="date"
                  label={t('common.to')}
                  value={packageServicesDateTo}
                  onChange={(e) => setPackageServicesDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPackageServicesDateFrom('');
                    setPackageServicesDateTo('');
                  }}
                >
                  {t('common.reset')}
                </Button>
              </Box>
            </Box>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.packageServicesData.length > 0 ? stats.packageServicesData : [{ name: 'No Data', value: 1 }]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.packageServicesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => `${value} requests`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Charts Row 2 */}
        <Card elevation={2} sx={{ mb: 4 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t('dashboard.requestsByStatus')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                type="date"
                label={t('common.from')}
                value={requestsDateFrom}
                onChange={(e) => setRequestsDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                type="date"
                label={t('common.to')}
                value={requestsDateTo}
                onChange={(e) => setRequestsDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setRequestsDateFrom('');
                  setRequestsDateTo('');
                }}
              >
                {t('common.reset')}
              </Button>
            </Box>
          </Box>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.requestsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="status" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#82ca9d" name="Number of Requests" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3 
        }}>
          <Card 
            elevation={2} 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/operations/overview')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">{t('dashboard.operations')}</Typography>
            </CardContent>
          </Card>
          <Card 
            elevation={2} 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/organization/overview')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
              <Typography variant="h6">{t('dashboard.organization')}</Typography>
            </CardContent>
          </Card>
          <Card 
            elevation={2} 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/finance/overview')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <AttachMoney sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6">{t('dashboard.finance')}</Typography>
            </CardContent>
          </Card>
          <Card 
            elevation={2} 
            sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }}
            onClick={() => navigate('/reports')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h6">{t('navigation.reports')}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
  );
};

export default Dashboard;
